import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import VideoCard from "@/components/feature/VideoCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { getCategoryGroup } from "@/mocks/categories";
import { getSubcategoryVideoCount, getVideosByType } from "@/utils/video";

export default function CategoryPage() {
  const { type = "" } = useParams();
  const group = getCategoryGroup(type);
  const list = group ? getVideosByType(group.type) : [];
  const preview = list.slice(0, 8);

  useDocumentMeta({
    title: group
      ? `${group.name}频道 - 逼特逼视频分类`
      : "视频分类 - 逼特逼视频",
    description: group
      ? `${group.name}频道收录${list.length}部内容：${group.description}`
      : "浏览逼特逼视频的电影、电视剧与短视频分类。",
    keywords: group
      ? `${group.name},${group.subcategories.map((sub) => sub.name).join(",")},逼特逼视频分类,在线观看`
      : "视频分类,逼特逼视频",
  });

  if (!group) {
    return (
      <div className="page-shell py-16 md:py-24">
        <EmptyState
          icon="ri-folder-unknow-line"
          title="暂无细分分类内容"
          description="这个分类可能已经调整，回到首页看看其他频道吧。"
        />
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-primary-400 hover:text-primary-300"
          >
            <i className="ri-arrow-left-line text-base" />
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const totalEpisodes = list.length;

  return (
    <div>
      <section className="relative overflow-hidden border-b border-background-200 bg-background-100">
        <div className="pointer-events-none absolute -right-16 -top-10 flex h-72 w-72 items-center justify-center text-background-300/30">
          <i className={`${group.icon} text-[280px]`} />
        </div>
        <div className="page-shell relative py-8 md:py-12">
          <Breadcrumb items={[{ label: `${group.name}频道` }]} />

          <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500/15 text-primary-400">
                  <i className={`${group.icon} text-lg`} />
                </span>
                <span className="font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">
                  {group.type} channel
                </span>
              </div>
              <h1 className="font-heading text-3xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                {group.name}频道
              </h1>
              <p className="mt-1 text-sm text-primary-400">{group.tagline}</p>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {group.description}
              </p>
            </div>

            <div className="flex items-center gap-6 text-sm">
              <div>
                <p className="font-heading text-2xl font-semibold text-foreground-950">
                  {totalEpisodes}
                </p>
                <p className="mt-1 text-xs text-foreground-500">在架内容</p>
              </div>
              <div className="h-9 w-px bg-background-300/70" />
              <div>
                <p className="font-heading text-2xl font-semibold text-foreground-950">
                  {group.subcategories.length}
                </p>
                <p className="mt-1 text-xs text-foreground-500">细分题材</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="page-shell py-8 md:py-12">
        <div className="mb-5 flex items-center justify-between gap-3">
          <h2 className="font-heading text-base font-medium text-foreground-900">细分分类</h2>
          <Link
            to={`/list/${group.type}`}
            className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-400"
          >
            查看全部{group.name}
            <i className="ri-arrow-right-line text-sm" />
          </Link>
        </div>

        {group.subcategories.length === 0 ? (
          <EmptyState title="暂无细分分类内容" description="该频道暂未设置细分类，可先浏览全部内容。" />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {group.subcategories.map((sub) => (
              <Link
                key={sub.id}
                to={`/list/${sub.id}`}
                className="group flex items-start justify-between gap-3 rounded-lg border border-background-200 bg-background-100 p-4 transition-all hover:-translate-y-0.5 hover:border-primary-400/70"
              >
                <div className="min-w-0">
                  <h3 className="font-heading text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-400">
                    {sub.name}
                    <span className="ml-2 font-body text-[11px] font-normal text-foreground-500">
                      {getSubcategoryVideoCount(sub.id)} 部
                    </span>
                  </h3>
                  <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-500">
                    {sub.description}
                  </p>
                </div>
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-background-200 text-foreground-500 transition-colors group-hover:border-primary-400 group-hover:text-primary-400">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="page-shell pb-12 md:pb-16">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-heading text-xl font-semibold tracking-tight text-foreground-950">
              {group.name}精选预览
            </h2>
            <p className="mt-1 text-sm text-foreground-600">
              先看几部代表作，更多内容在分类列表中。
            </p>
          </div>
          <Link
            to={`/list/${group.type}`}
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-400"
          >
            全部{group.name}
            <i className="ri-arrow-right-line text-base" />
          </Link>
        </div>

        {preview.length === 0 ? (
          <EmptyState title="暂无推荐视频，敬请期待" description="该频道内容正在整理中。" />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {preview.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-400"
          >
            <i className="ri-arrow-left-line text-base" />
            返回首页
          </Link>
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            其他频道：
            {["movie", "series", "short"]
              .filter((item) => item !== group.type)
              .map((item) => (
                <Link
                  key={item}
                  to={`/category/${item}`}
                  className="whitespace-nowrap rounded border border-background-200 px-2 py-1 text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-400"
                >
                  {item === "movie" ? "电影" : item === "series" ? "电视剧" : "短视频"}
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}